/* eslint-disable max-lines */
import React, { useRef, useEffect, TouchEvent } from 'react';
import { cx, LinariaClassName } from 'linaria';
import { PlayVideo } from '../play-video';
import { MediaList } from '../types';
import {
  container,
  footer,
  footerMargin,
  itemWrapper,
  listWrapper,
  playVideo,
  thumb,
  thumbMargin,
  thumbCircle,
  thumbCircleActive,
  thumbCircleSmall,
  thumbCircleSmaller,
  thumbTriangle,
  thumbTriangleActive,
  thumbTriangleSmall,
  thumbTriangleSmaller,
  thumbs,
  thumbsWrapper,
} from './styles';
import { Picture } from '../preview/picture';
import { VideoMobile } from '../preview/video-mobile';
import {
  START_TRANSITION,
  THUMB_WIDTH,
  TRIM_COUNT,
  getSwipeDirection,
  isSmallThumb,
  isSmallerThumb,
} from './utils';
import {
  DATA_QA_MOBILE_GALLERY_ITEM_WRAPPER,
  DATA_QA_MOBILE_GALLERY_THUMB_CIRCLE,
  DATA_QA_MOBILE_GALLERY_THUMB_TRIANGLE,
  DATA_QA_MOBILE_GALLERY_ROOT_CONTAINER,
  DATA_QA_MOBILE_GALLERY_LIST_WRAPPER,
  DATA_QA_MOBILE_GALLERY_THUMBS_WRAPPER,
} from '../constants';

type Props = {
  active: number;
  data: MediaList;
  displayedName: string;
  onClick?: (mediaType: string, imgSrc: string) => void;
  setActive: (index: number) => void;
  onMediaLoad?: (mediaType: string, imgSrc: string, loadTime?: number) => void;
  style?: LinariaClassName;
};

function MobileGallery({ active, data, displayedName, onClick, onMediaLoad, setActive, style }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const thumbsRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isTrim = data.length > TRIM_COUNT;
  const videoIsActive = data[active]?.type === 'video';
  const indexVideo = data.findIndex(({ type }) => type === 'video');

  function handleTouchStart(event: TouchEvent) {
    touchStartX.current = event.changedTouches[0].clientX;
    touchStartY.current = event.changedTouches[0].clientY;
  }

  function handleTouchEnd(event: TouchEvent) {
    if (!listRef.current || !ref.current) return;

    const touchEndX = event.changedTouches[0].clientX;
    const touchEndY = event.changedTouches[0].clientY;
    const dx = Math.round(touchEndX) - touchStartX.current;
    const dy = Math.round(touchEndY) - touchStartY.current;

    if (getSwipeDirection(dx, dy) === 'vertical') return;

    if (touchStartX.current > touchEndX) {
      const isLast = active === data.length - 1;
      const value = isLast ? active : active + 1;

      if (isLast) {
        const transition = ref.current?.offsetWidth * active;
        listRef.current?.style.setProperty('transform', `translateX(-${transition}px)`);
      }
      setActive(value);
    }
    if (touchStartX.current < touchEndX) {
      const value = active === 0 ? active : active - 1;

      if (value === 0) {
        listRef.current?.style.setProperty('transform', `translateX(0px)`);
      }
      setActive(value);
    }
  }

  function handleTouchMove(event: TouchEvent) {
    if (!listRef.current || !ref.current) return;

    const dx = Math.round(event.touches[0].clientX) - touchStartX.current;
    const dy = Math.round(event.touches[0].clientY) - touchStartY.current;
    const transition = -ref.current?.offsetWidth * active + dx;

    if (getSwipeDirection(dx, dy) === 'horizontal') {
      listRef.current?.style.setProperty('transform', `translateX(${transition}px)`);
    }
  }

  function goFirstVideo() {
    setActive(indexVideo);
    onClick?.(data[indexVideo].type, data[indexVideo].media.url || '');

    if (!thumbsRef?.current) return;

    if (isTrim && indexVideo - active > 1) {
      const index = indexVideo === data.length - 1 ? indexVideo - 4 : indexVideo - 3;
      const transition = THUMB_WIDTH * index;
      thumbsRef.current?.style.setProperty('transform', `translateX(-${transition}px)`);
    }
  }

  useEffect(() => {
    if (listRef?.current) {
      const width = ref.current?.offsetWidth || 0;
      const transition = width * active;
      listRef.current?.style.setProperty('transform', `translateX(-${transition}px)`);
    }
    if (thumbsRef?.current && isTrim && active >= START_TRANSITION && active < data.length - 2) {
      const transition = THUMB_WIDTH * (active - START_TRANSITION);
      thumbsRef.current?.style.setProperty('transform', `translateX(-${transition}px)`);
    }
  }, [active, data.length, isTrim]);

  return (
    <div ref={ref} className={style} data-qa={DATA_QA_MOBILE_GALLERY_ROOT_CONTAINER}>
      <div className={container}>
        <div ref={listRef} className={listWrapper} data-qa={DATA_QA_MOBILE_GALLERY_LIST_WRAPPER}>
          {data.map(({ media, type }, idx) => {
            const photoNumber = idx !== 0 ? `${idx + 1} ` : '';
            const altValue = `Фото ${photoNumber}${displayedName}`;
            const src = media.url ?? '';
            // eslint-disable-next-line i18next/no-literal-string
            const fetchpriority = idx === 0 ? 'high' : 'low';

            return (
              <div
                key={src}
                className={itemWrapper}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                onTouchMove={handleTouchMove}
                data-qa={DATA_QA_MOBILE_GALLERY_ITEM_WRAPPER}
              >
                {type === 'image' && (
                  <Picture
                    alt={altValue}
                    src={src}
                    onMediaLoad={onMediaLoad}
                    fetchpriority={fetchpriority}
                  />
                )}
                {type === 'video' && <VideoMobile src={src} onMediaLoad={onMediaLoad} />}
              </div>
            );
          })}
        </div>
      </div>
      <div className={cx(footer, indexVideo > -1 && footerMargin)}>
        {data.length > 1 && (
          <>
            {indexVideo > -1 && (
              <div className={playVideo}>
                <PlayVideo isActive={videoIsActive} onClick={goFirstVideo} />
              </div>
            )}
            <div className={thumbs}>
              <div ref={thumbsRef} className={thumbsWrapper} data-qa={DATA_QA_MOBILE_GALLERY_THUMBS_WRAPPER}>
                {data.map(({ type, media }, index) => {
                  const isActive = index === active;
                  const isSmall = isTrim && isSmallThumb(active, data.length, index);
                  const isSmaller = isTrim && isSmallerThumb(active, data.length, index);

                  return (
                    <div className={cx(thumb, isTrim && thumbMargin)} key={media.url}>
                      {type === 'image' && (
                        <div
                          className={cx(
                            thumbCircle,
                            isActive && thumbCircleActive,
                            isSmall && thumbCircleSmall,
                            isSmaller && thumbCircleSmaller,
                          )}
                          data-qa={DATA_QA_MOBILE_GALLERY_THUMB_CIRCLE}
                        />
                      )}
                      {type === 'video' && (
                        <div
                          className={cx(
                            thumbTriangle,
                            isActive && thumbTriangleActive,
                            isSmall && thumbTriangleSmall,
                            isSmaller && thumbTriangleSmaller,
                          )}
                          data-qa={DATA_QA_MOBILE_GALLERY_THUMB_TRIANGLE}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { MobileGallery };
